import {
  ensureClient,
  initializeSogniClient,
  signupSogniClient,
  disconnectSogniClient,
  tryRestoreFromCookies,
  getSogniAuthUrl,
  getBalance as getSogniBalance,
  getModels as getSogniModels,
  getVideoModels as getSogniVideoModels,
  isClientReady,
  clearLoggedInMark,
  onAccountUpdate,
  getAccountSnapshot,
  fetchMe,
  resendVerificationEmail
} from '../sogni-client.svelte.js';
import { loadUserSettings, clearUserSettings } from './settings.svelte.js';

/**
 * Authentication State
 *
 * Thin UI-facing mirror of the Sogni SDK's observable account. Cross-domain SSO
 * is handled by the SDK's cookie auth (see sogni-client.svelte.js); this module
 * only reflects that state for components and drives login/signup/logout.
 */

export const auth = $state({
  isLoggedIn: false,
  username: null,
  walletAddress: null,
  email: null,
  balance: { spark: 0, sogni: 0, premiumSpark: 0 },
  isUnlimited: false,
  subscription: null,
  sensitiveContentFilterUnlocked: null, // socket's answer; null until known
  models: [],
  videoModels: [],
  videoModelsStatus: 'idle', // idle | loading | ready | error
  isLoading: false, // during an explicit login/signup action
  error: null,
  isRestoring: true, // true during the initial SSO probe on boot
  showLoginModal: false,
  authMode: 'login', // 'login' | 'signup' — initial view of the auth modal
  emailVerified: null, // null = unknown, true/false once fetched
  emailBannerDismissed: false
});

let lastSyncedUser = null;

// Mirror the SDK account (socket-driven: balance, subscription, login state).
onAccountUpdate((snap) => {
  auth.isLoggedIn = snap.authenticated;
  auth.username = snap.username;
  auth.walletAddress = snap.walletAddress;
  auth.email = snap.email;
  auth.balance = snap.balance;
  auth.isUnlimited = snap.isUnlimited;
  auth.subscription = snap.subscription;
  auth.sensitiveContentFilterUnlocked = snap.sensitiveContentFilterUnlocked ?? null;

  // Load per-user settings once when a user becomes active; clear on logout.
  if (snap.authenticated && snap.username && snap.username !== lastSyncedUser) {
    lastSyncedUser = snap.username;
    auth.videoModels = [];
    auth.videoModelsStatus = 'idle';
    loadUserSettings(snap.username);
    // Fetch email-verification status once per user (best-effort). Guard against
    // a late response from a previous user overwriting the current one.
    const forUser = snap.username;
    fetchMe().then((me) => {
      if (me && auth.username === forUser) auth.emailVerified = me.emailVerified;
    });
  } else if (!snap.authenticated && lastSyncedUser) {
    lastSyncedUser = null;
    auth.videoModels = [];
    auth.videoModelsStatus = 'idle';
    auth.emailVerified = null;
    auth.emailBannerDismissed = false;
  }
});

/**
 * Resend the verification email for the current account.
 */
export async function resendEmailVerification() {
  try {
    await resendVerificationEmail();
    return true;
  } catch (error) {
    console.error('Failed to resend verification email:', error);
    return false;
  }
}

/**
 * Log in with Sogni credentials. Session persists via cookie and propagates to
 * every *.sogni.ai app.
 */
export async function login(username, password, rememberMe = true) {
  auth.isLoading = true;
  auth.error = null;
  try {
    const result = await initializeSogniClient(username, password, rememberMe);
    if (result.success) {
      auth.models = result.models || [];
      auth.videoModels = [];
      auth.videoModelsStatus = 'idle';
      return true;
    }
    auth.error = result.error || 'Login failed';
    return false;
  } catch (error) {
    console.error('Login error:', error);
    auth.error = error.message || 'Login failed';
    return false;
  } finally {
    auth.isLoading = false;
  }
}

/**
 * Create a new Sogni account (requires a Cloudflare Turnstile token).
 */
export async function signup({ username, email, password, subscribe = false, turnstileToken, referralCode }) {
  auth.isLoading = true;
  auth.error = null;
  try {
    const result = await signupSogniClient(
      { username, email, password, subscribe, turnstileToken, referralCode },
      true
    );
    if (result.success) {
      auth.models = result.models || [];
      auth.videoModels = [];
      auth.videoModelsStatus = 'idle';
      return true;
    }
    auth.error = result.error || 'Could not create account';
    return false;
  } catch (error) {
    console.error('Signup error:', error);
    auth.error = error.message || 'Could not create account';
    return false;
  } finally {
    auth.isLoading = false;
  }
}

/**
 * Redirect to app.sogni.ai for auth (fallback when in-app auth is not desired
 * or the origin is not a *.sogni.ai subdomain).
 */
export function redirectToSogniLogin() {
  window.location.href = getSogniAuthUrl();
}

/**
 * Log out — clears the shared *.sogni.ai session across all apps.
 */
export async function logout() {
  try {
    await disconnectSogniClient();
  } catch (error) {
    console.error('Logout error:', error);
  }
  clearLoggedInMark();
  clearUserSettings();
  // onAccountUpdate has already reset the mirrored fields; ensure models cleared.
  auth.models = [];
  auth.videoModels = [];
  auth.videoModelsStatus = 'idle';
  auth.error = null;
}

/**
 * Bootstrap: probe the shared cookie session on app start. Runs unconditionally
 * so a login on any *.sogni.ai app is picked up here (SSO).
 */
export async function restoreSession({ silent = false } = {}) {
  // `silent` re-probes (e.g. on tab refocus) must not toggle isRestoring, or the
  // LOGIN button and guest banner would flicker on every focus.
  if (!silent) auth.isRestoring = true;
  try {
    const result = await tryRestoreFromCookies();
    if (result.success) {
      auth.models = result.models || [];
      return true;
    }
    return false;
  } catch (error) {
    console.error('Session restore error:', error);
    return false;
  } finally {
    if (!silent) auth.isRestoring = false;
  }
}

export async function refreshBalance() {
  if (!isClientReady()) return;
  try {
    auth.balance = await getSogniBalance();
  } catch (error) {
    console.error('Balance refresh error:', error);
  }
}

export async function refreshModels() {
  if (!isClientReady()) return;
  try {
    auth.models = await getSogniModels();
  } catch (error) {
    console.error('Models refresh error:', error);
  }
}

/** Refresh the live video catalog without mixing encoders into image settings. */
export async function refreshVideoModels() {
  if (!auth.isLoggedIn || !isClientReady()) return false;

  const username = auth.username;
  auth.videoModelsStatus = 'loading';
  try {
    const models = await getSogniVideoModels();
    if (!auth.isLoggedIn || auth.username !== username) return false;
    auth.videoModels = models;
    auth.videoModelsStatus = 'ready';
    return true;
  } catch (error) {
    if (!auth.isLoggedIn || auth.username !== username) return false;
    console.error('Video models refresh error:', error);
    auth.videoModelsStatus = 'error';
    return false;
  }
}

export function clearError() {
  auth.error = null;
}

export function openLoginModal() {
  auth.authMode = 'login';
  auth.error = null;
  auth.showLoginModal = true;
}

export function openSignupModal() {
  auth.authMode = 'signup';
  auth.error = null;
  auth.showLoginModal = true;
}

export function setAuthMode(mode) {
  auth.authMode = mode === 'signup' ? 'signup' : 'login';
  auth.error = null;
}

export function closeLoginModal() {
  auth.showLoginModal = false;
}

export function isSogniReady() {
  return isClientReady();
}

// Warm up the client singleton early so the socket/models are ready ASAP.
ensureClient().catch(() => {});
